import { GameType } from '@daymade/common-lib/dbEnums';
import LoadingOverlay from 'components/layout/LoadingOverlay';
import MainLayout from 'components/layout/MainLayout';
import ALittleLostSection from 'components/token-town/a-little-lost-section';
import HeroFooter from 'components/token-town/draws/hero-footer/HeroFooter';
import ImageGallery from 'components/token-town/draws/image-gallery/ImageGallery';
import InfoSection from 'components/token-town/draws/info-section/InfoSection';
import PurchaseEntries from 'components/token-town/draws/purchase-entries/PurchaseEntries';
import Header from 'components/token-town/header/Header';
import BackLink from 'components/token-town/links/BackLink';
import TermsAndConditionsLink from 'components/token-town/links/TermsAndConditionsLink';
import PageGrid from 'components/token-town/page-grid/PageGrid';
import Page from 'components/token-town/page/Page';
import PurchaseTokens from 'components/token-town/purchase-tokens';
import PurchaseTokensBanner from 'components/token-town/purchase-tokens-banner/PurchaseTokensBanner';
import YouMightAlsoLikeSection from 'components/token-town/YouMightAlsoLikeSection';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useTokenRedemptionOption } from 'hooks/token-town/useTokenRedemptionOption';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { tokenApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';

const TokenTownDrawPage = observer(() => {
  const router = useRouter();
  const { slug: routerSlug } = router.query;
  const slug = Array.isArray(routerSlug) ? routerSlug[0] : routerSlug;

  if (!slug) {
    router?.replace('/404');
  }

  const { loading, error, tokenRedemptionOption } =
    useTokenRedemptionOption(slug);

  const [showPurchaseTokens, setShowPurchaseTokens] = useState(false);

  const { displayToast } = useUIStore();

  const { currentUser, balances, updateCurrentUser, activeTokenTownTickets } =
    useUserStore();
  const playerTokenCount = balances?.tokenBalance ?? 0;

  useEffect(() => {
    const isMissingDrawData =
      !loading &&
      (!slug ||
        !tokenRedemptionOption ||
        !tokenRedemptionOption.drawDetails ||
        !tokenRedemptionOption.drawDetails.prize);

    if (isMissingDrawData) {
      router.replace('/404');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenRedemptionOption, loading]);

  if (loading) {
    return <LoadingOverlay />;
  }

  if (
    !slug ||
    !tokenRedemptionOption ||
    !tokenRedemptionOption.drawDetails ||
    !tokenRedemptionOption.drawDetails.prize
  ) {
    return null;
  }

  if (error) {
    return <>An error occurred: {error}</>;
  }

  const {
    id: tokenRedemptionOptionId,
    title: optionTitle,
    price,
  } = tokenRedemptionOption;

  const {
    numberSetup,
    entryCap,
    numEntries,
    nextDrawId,
    nextDrawDate,
    gameType,
    schedule,
    maxPlayerEntries,
    drawTypeCode,
  } = tokenRedemptionOption.drawDetails;

  const {
    title: prizeTitle,
    imageLocations: { main, secondary1, secondary2 },
    overviewBody,
    whatsIncludedBody,
  } = tokenRedemptionOption.drawDetails.prize;

  const tokenCost = price ?? 0;
  const title = prizeTitle || optionTitle;

  const handleRedemption = async (
    mainNumbers: number[],
    numOfEntries: number,
  ) => {
    const resp = await tokenApiClient.auth.redeemWithToken({
      params: { id: tokenRedemptionOptionId },
      body: {
        ENTRY: {
          entries: Array.from({ length: numOfEntries }, () => ({
            type: GameType.LOTTERY,
            main: mainNumbers,
            bonus: [],
          })),
          drawId: nextDrawId || undefined,
        },
      },
    });

    switch (resp.status) {
      case HttpStatusCode.OK:
        await updateCurrentUser();
        router.push(`/token-town/confirmation?id=${resp.body.id}`);
        break;

      case HttpStatusCode.BAD_REQUEST:
        displayToast({
          title: resp.body.message,
          color: '#FF6600',
          timeout: 5000,
        });
        break;

      default:
        break;
    }
  };

  return (
    <MainLayout>
      <Header />
      <Page>
        {showPurchaseTokens && (
          <PurchaseTokens
            onClose={() => setShowPurchaseTokens(false)}
            tokenBalance={playerTokenCount}
          />
        )}

        {currentUser && (
          <PurchaseTokensBanner
            onClick={() => setShowPurchaseTokens(true)}
            tokenCount={playerTokenCount}
          />
        )}

        <PageGrid>
          <ImageGallery
            tokenCost={tokenCost}
            images={[main, secondary1 ?? undefined, secondary2 ?? undefined]}
            heroFooter={
              <HeroFooter isInstantRaffle={!schedule} drawDate={nextDrawDate} />
            }
          />
          <PurchaseEntries
            maxPlayerEntries={maxPlayerEntries ?? undefined}
            existingTickets={activeTokenTownTickets}
            userTokenBalance={balances?.tokenBalance ?? 0}
            tokenCost={tokenCost}
            title={title ?? ''}
            numberRanges={numberSetup || undefined}
            entryCap={entryCap}
            entriesSold={numEntries ?? 0}
            drawSchedule={schedule}
            drawTypeCode={drawTypeCode}
            gameType={gameType!}
            handleRedemption={handleRedemption}
            setShowPurchaseTokens={() => setShowPurchaseTokens(true)}
          />
        </PageGrid>

        <InfoSection
          title={prizeTitle}
          description={overviewBody ?? ''}
          includes={whatsIncludedBody ?? ''}
        />

        <TermsAndConditionsLink />
        <BackLink to="games" />
      </Page>
      <YouMightAlsoLikeSection tokenBalance={playerTokenCount} backTo="games" />
      <ALittleLostSection />
    </MainLayout>
  );
});

export default TokenTownDrawPage;
